import { UIText1, Image, UIHeading5 } from '@cian/ui-kit';
import { mergeStyles } from '@cian/utils/lib/shared/style';
import classNames from 'classnames';
import * as React from 'react';

import * as styles from './ChatMessageLayout.css';
import { useUserColor } from './hooks';
import { PrettyDateContainer } from '../../containers/PrettyDateContainer';

type TChatMessageLayoutProps = {
  messageUserId: number;
  isOutcoming: boolean;
  isUnread: boolean;
  isOnlyImage: boolean;
  isOffer: boolean;
  isLastInChain: boolean;
  isTextAndImage: boolean;
  created: number;
  imagesNode: React.ReactNode | null;
  textNode: React.ReactNode | null;
  actionButtonsNode: React.ReactNode | null;
  statusNode: React.ReactNode | null;
  errorSignNode: React.ReactNode | null;
  errorTextNode: React.ReactNode | null;
  errorMenuNode: React.ReactNode | null;
  infoMessageNode: React.ReactNode | null;
  documentNode: React.ReactNode | null;
  imageAsDocumentNode: React.ReactNode | null;
  offerNode: React.ReactNode | null;
  bubblesNode: React.ReactNode | null;
  userName?: string;
  nameReplacementImageUrl?: string;
  showDailyrentReviewButtons?: boolean;
};

export const ChatMessageLayout: React.FC<TChatMessageLayoutProps> = ({
  messageUserId,
  isOutcoming,
  isUnread,
  isOnlyImage,
  isOffer,
  isLastInChain,
  isTextAndImage,
  created,
  imagesNode,
  textNode,
  actionButtonsNode,
  statusNode,
  errorSignNode,
  errorTextNode,
  errorMenuNode,
  infoMessageNode,
  documentNode,
  imageAsDocumentNode,
  offerNode,
  bubblesNode,
  userName,
  nameReplacementImageUrl,
  showDailyrentReviewButtons = false,
}) => {
  const userColor = useUserColor(messageUserId);

  const actionButtonsClassName = showDailyrentReviewButtons
    ? styles['dailyrent-action_buttons']
    : styles['action_buttons'];

  return (
    <div
      className={classNames(styles['container'], isOutcoming && styles['container_outcoming'])}
      data-name="ChatMessageLayout"
    >
      <div className={classNames(styles['bubble'], imagesNode && styles['bubble_with_image'])}>
        <div className={styles['row']}>
          <div
            className={classNames(
              styles['message'],
              isOutcoming ? styles['outcoming'] : styles['incoming'],
              isUnread && styles['unread'],
              isOnlyImage && styles['image_message'],
              isLastInChain && styles['message_tail'],
              isTextAndImage && styles['image_with_text'],
              (isOffer || imageAsDocumentNode) && styles['image_as_document'],
              documentNode && styles['document'],
              styles[`message_theme_${userColor}`],
            )}
          >
            <div className={styles['content']}>
              {userName && !nameReplacementImageUrl && (
                <div className={classNames(styles['user_name'], styles[`user_name_${userColor}`])}>
                  <UIHeading5>{userName}</UIHeading5>
                </div>
              )}
              {nameReplacementImageUrl && (
                <div className={styles['name_replacement_image']}>
                  <Image height={18} src={nameReplacementImageUrl} display="inline-block" />
                </div>
              )}
              {imagesNode}
              {textNode && (
                <span className={styles['text_message']} data-name="ChatMessageLayout_text">
                  <UIText1 color={isOutcoming ? 'white_100' : 'black_100'}>{textNode}</UIText1>
                </span>
              )}
              {actionButtonsNode && (
                <div className={actionButtonsClassName} data-name="ChatMessageLayout_action_buttons">
                  {actionButtonsNode}
                </div>
              )}
            </div>
            {documentNode}
            {offerNode}
            {imageAsDocumentNode}
            <div className={styles['info']} data-name="ChatMessageLayout_info">
              <PrettyDateContainer
                date={created}
                showOnlyTime
                containerStyles={mergeStyles(styles['info_item'], styles['info_date'])}
              />
              <span className={styles['info_item']} data-name="ChatMessageLayout_status">
                {statusNode}
              </span>
            </div>
          </div>
          <div className={styles['error_sign']}>{errorSignNode}</div>
        </div>
        <div className={styles['error_text']}>{errorTextNode}</div>
        {errorMenuNode}
      </div>
      {infoMessageNode}
      {bubblesNode}
    </div>
  );
};
